import React, { FC, useMemo } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { convertCryptoToUsd, selectToFiatExchangeRate } from 'shared/utils';
import { FormatCurrency, FormatCurrencyProps } from './FormatCurrency/FormatCurrency';
import { FormatCurrencyType } from './FormatCurrency/format-currency-type.enum';
import { exchangeRatesSelector } from 'modules/payments/store/exchange-rates/selectors';
import { SystemCurrency } from '@okletsplay/core';

export type FormatCurrenciesProps = Omit<
  FormatCurrencyProps,
  'type' | 'inParentheses' | 'showSign'
> & {
  direction?: 'row' | 'column';
  showUsdEquivalent?: boolean;
};

export const FormatCurrencies: FC<FormatCurrenciesProps> = ({
  amount,
  direction = 'row',
  showUsdEquivalent = true,
  ...rest
}) => {
  const exchangeRates = useSelector(exchangeRatesSelector.all);

  const fiatExchangeRate = rest.currency
    ? selectToFiatExchangeRate(exchangeRates, rest.currency as SystemCurrency)
    : 0;

  const fiatAmount = useMemo(
    () => convertCryptoToUsd(amount, fiatExchangeRate),
    [fiatExchangeRate, amount]
  );

  const needShowUsdEquivalent = showUsdEquivalent && !(rest.showZeroLabel && amount === 0);

  return (
    <Box
      component="span"
      sx={
        direction === 'row'
          ? { display: 'inline-block', textAlign: 'right' }
          : { display: 'flex', flexDirection: direction, textAlign: 'right' }
      }
    >
      <FormatCurrency amount={amount} type={FormatCurrencyType.Crypto} {...rest} />{' '}
      {needShowUsdEquivalent ? (
        <FormatCurrency
          amount={fiatAmount}
          type={FormatCurrencyType.Fiat}
          inParentheses
          showSign={false}
          {...rest}
        />
      ) : null}
    </Box>
  );
};
